import type { FC } from 'react'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { Close } from '@dhl/icons'
import { Box, IconButton, Sx } from '@dhl/theme'
import { RichTooltip } from './RichTooltip'
import { getFirstTitle } from './prismic/prismicHelpers'
import { WhatsNewDocument } from './model/types'

type Props = {
  document?: WhatsNewDocument
  open: boolean
  children: ReactElement
  onReadMoreClick: () => void
  onCloseClick: () => void
}

const classes: Record<string, Sx> = {
  container: {
    overflow: 'hidden',
  },
  closeIconButton: {
    padding: (theme) => theme.spacing(1),
    position: 'absolute',
    right: 3,
    top: 3,
  },
  closeIcon: {
    fontSize: 14,
    color: (theme) => theme.palette.primary.main,
  },
  title: {
    fontSize: '0.8rem',
    fontWeight: 'bold',
    color: (theme) => theme.palette.grey[900],
  },
  body: {
    fontSize: '0.8rem',
    color: (theme) => theme.palette.grey[50],
    lineHeight: '140%',
    marginTop: 10,
    marginBottom: 2,
    cursor: 'pointer',
  },
  link: {
    color: (theme) => theme.palette.error.dark,
  },
}

const getBody = (document?: WhatsNewDocument) => {
  if (!document) {
    return ''
  }

  const MAX_LENGTH = 90
  // Get document title and remove trailing dot
  const title = getFirstTitle(document).replace(/\.$/, '')
  return title.length > MAX_LENGTH ? `${title.substr(0, MAX_LENGTH)}...` : `${title}.`
}

export const WhatsNewTooltip: FC<Props> = ({ document, open, children, onReadMoreClick, onCloseClick }) => {
  const { t } = useTranslation()
  const body = getBody(document)

  const tooltipContent = (
    <Box sx={classes.container}>
      <IconButton onClick={onCloseClick} sx={classes.closeIconButton} size="large">
        <Close sx={classes.closeIcon} data-test="close-whats-new" />
      </IconButton>
      <Box sx={classes.title}>{t('widgets.whatsnew.popover.title')}</Box>
      {}
      <Box sx={classes.body} onClick={onReadMoreClick}>
        {body}
        &nbsp;
        <Box component="span" sx={classes.link}>
          {t('widgets.whatsnew.popover.readmore')}
          ...
        </Box>
      </Box>
    </Box>
  )

  return (
    <RichTooltip
      width={283}
      top={14}
      left={-61}
      arrowMarginLeft={130}
      content={tooltipContent}
      open={open}
      placement="bottom"
      data-test="new-news"
    >
      {children}
    </RichTooltip>
  )
}
